
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Globe, ArrowUpDown } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import AdminEventsList from "@/components/admin/AdminEventsList";
import { AddEventDialog } from "@/components/admin/AddEventDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ScrapedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  is_published: boolean;
  scraped_at: string;
  ticket_status: string | null;
  last_status_check: string | null;
}

const Events = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [events, setEvents] = useState<ScrapedEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"created_at" | "date">("created_at");

  useEffect(() => {
    fetchEvents();
  }, [sortBy]);

  const fetchEvents = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("scraped_events")
      .select("*")
      .order(sortBy, { ascending: sortBy === "date" });
    
    if (data && !error) {
      setEvents(data);
    }
    setIsLoading(false);
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin-dashboard"
              className="flex items-center gap-2 text-primary-foreground hover:text-white/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            {isRTL ? "إدارة الفعاليات" : "Events Management"}
          </h1>
          <div className="flex items-center gap-3 sm:gap-4">
            <AddEventDialog onEventAdded={fetchEvents} />
            <span className="text-white/50 text-sm">
              {isRTL ? `${events.length} فعالية` : `${events.length} events`}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {isRTL ? "ترتيب حسب:" : "Sort by:"}
          </span>
          <Select value={sortBy} onValueChange={(value: "created_at" | "date") => setSortBy(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">
                {isRTL ? "وقت الإضافة (الأحدث)" : "Added (Newest)"}
              </SelectItem>
              <SelectItem value="date">
                {isRTL ? "تاريخ الفعالية (الأقرب)" : "Event Date (Soonest)"}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <AdminEventsList 
          events={events}
          setEvents={setEvents}
          isLoading={isLoading}
          showCategory={true}
        />
      </main>
    </AdminPageBackground>
  );
};

export default Events;
